import { createTheme } from "@mui/material/styles";

export const createShessaiTheme = ({
  mode,
  primaryColorLight = "#0399FF",
  primaryColorDark = "#0399FF",
  appLogoLight,
  appLogoDark,
  appLogoFavicon,
  appName,
  locale,
  defaultLogoLight,
  defaultLogoDark,
}) => {
  const isLight = mode === "light";
  const primary = isLight ? primaryColorLight : primaryColorDark;

  return createTheme(
    {
      // ── Scrollbars ──────────────────────────────────────────────────────
      scrollbarStyles: {
        "&::-webkit-scrollbar": { width: "4px", height: "4px" },
        "&::-webkit-scrollbar-thumb": {
          background: isLight ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.15)",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": { background: "transparent" },
      },
      scrollbarStylesSoft: {
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-thumb": {
          background: isLight ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.15)",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": { background: "transparent" },
      },

      // ── Palette ─────────────────────────────────────────────────────────
      palette: {
        mode: mode,
        primary: {
          main: primary,
          light: primary + "80",
          dark: "#0284d4",
          contrastText: "#ffffff",
        },
        secondary: { main: "#2AD1C9", contrastText: "#ffffff" },
        error: { main: "#F04060" },
        textPrimary: primary,
        borderPrimary: primary,
        dark: { main: isLight ? "#333333" : "#F3F3F3" },
        light: { main: isLight ? "#F3F3F3" : "#333333" },
        fontColor: primary,
        tabHeaderBackground: isLight
          ? "rgba(3,153,255,0.06)"
          : "rgba(3,153,255,0.08)",
        optionsBackground: isLight ? "#ffffff" : "#0B0F1A",
        fancyBackground: isLight ? "#ffffff" : "#0B0F1A",
        total: isLight ? "#fff" : "#0f172a",
        messageIcons: isLight ? "grey" : "#F3F3F3",
        inputBackground: isLight ? "#f8f7f5" : "#0f172a",
        barraSuperior: isLight ? primary : "#0B0F1A",
        background: {
          default: isLight ? "#ffffff" : "#0B0F1A",
          paper: isLight ? "#ffffff" : "#0f172a",
        },
        text: {
          primary:  isLight ? "rgba(0,0,0,0.82)" : "rgba(255,255,255,0.88)",
          secondary: isLight ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.45)",
          disabled:  isLight ? "rgba(0,0,0,0.25)" : "rgba(255,255,255,0.25)",
          hint:      isLight ? "rgba(0,0,0,0.25)" : "rgba(255,255,255,0.25)",
        },
        bgPanel:     isLight ? "#ffffff"          : "#0B0F1A",
        bgSearchBar: isLight ? "#f8f7f5"          : "#0f172a",
        bgTabsBar:   isLight ? "rgba(0,0,0,0.02)" : "rgba(255,255,255,0.02)",
        // ── Chat message bubbles ──────────────────────────────────────────
        msgSentBg:        isLight ? "#d9fdd3"    : "#005c4b",
        msgRecvBg:        isLight ? "#ffffff"    : "#1e2a35",
        msgSystemBg:      isLight ? "#f7f7f5"    : "#1d282f",
        msgHighlightBg:   isLight ? "#fef9c3"    : "#3d3200",
        msgHighlightText: isLight ? "#713f12"    : "#fde68a",
        msgForwardBg:     isLight ? "#cfe9ba"    : "#025144",
        msgQuoteBg:       isLight ? "#E1F5FE"    : "rgba(255,255,255,0.04)",
        msgTextPrimary:   isLight ? "rgba(0,0,0,0.82)" : "#e8edf2",
        chatAreaBg:       isLight ? "#ffffff"    : "#0B0F1A",
        msgTimestamp:     isLight ? "rgba(0,0,0,0.35)"  : "#6b7f8c",
        msgMuted:         "#999999",
        msgEmptyBorder:   isLight ? "#e0e0e0"    : "#555555",
        msgQuoteText:     isLight ? "rgba(0,0,0,0.72)" : "#e2e8f0",
        msgErrorColor:    "#f55d65",

        // ── Ticket status badges ──────────────────────────────────────────
        badgeWarnBg:    "#facc15",
        badgeUrgentBg:  "#f87171",
        badgeUrgentText:"#ffffff",
        badgeWarnText:  "#000000",
        statusSuccess:  "#22c55e",
        statusTransfer: "#a855f7",
        statusMedia:    "#388aff",
        statusAudio:    "#35cd96",
        statusInfo:     "#6bcbef",

        // ── Navigation ───────────────────────────────────────────────────
        navItemText:    isLight ? "rgba(0,0,0,0.65)"  : "rgba(255,255,255,0.65)",
        navActiveColor: isLight ? primaryColorLight    : "#2AD1C9",
        navTooltipBg:   isLight ? "rgba(0,0,0,0.8)"   : "rgba(0,0,0,0.8)",
        tagInternal:    "#0f4c81",
        // ── Login / Forms ────────────────────────────────────────────────
        loginFormBg:    isLight ? "#ffffff"    : "#0f172a",
        loginTitleText: isLight ? "rgba(0,0,0,0.82)" : "#f1f5f9",
        loginSubText:   isLight ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.45)",
        loginInputText: isLight ? "rgba(0,0,0,0.82)" : "#e2e8f0",
        loginLinkColor: isLight ? "rgba(0,0,0,0.65)" : "rgba(255,255,255,0.65)",
        // ── Misc status ──────────────────────────────────────────────────
        statusAmber:    isLight ? "#b45309" : "#f59e0b",
        // ── Schedule status chips ─────────────────────────────────────────
        schedPendingBg:   isLight ? "#fef3c7"          : "rgba(247,144,9,0.15)",
        schedPendingText: isLight ? "#92400e"          : "#f59e0b",
        schedSentBg:      isLight ? "#dcfce7"          : "rgba(34,197,94,0.15)",
        schedSentText:    isLight ? "#166534"          : "#22c55e",
        schedErrorBg:     isLight ? "#fee2e2"          : "rgba(239,68,68,0.15)",
        // ── MessageInput ──────────────────────────────────────────────────
        msgReplyBg:       isLight ? "#f8f7f5"          : "#1a2030",
        msgInputBg:       isLight ? "#ffffff"          : "#0f172a",
        msgInputBorder:   isLight ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.08)",
        audioWaveColor:   "#7f66ff",
        msgLinkColor:     isLight ? "#0369a1"          : "#7dd3fc",
        // ── ChatMessages ─────────────────────────────────────────────────
        chatSidebarBg:    isLight ? "#ffffff"           : "#0B0F1A",
        chatRecvBg:       isLight ? "#ffffff"           : "#1e2a35",
        chatSentBg:       isLight ? "#dcf8c6"           : "#005c4b",
        chatTextBg:       isLight ? "rgba(0,0,0,0.82)" : "#e2e8f0",
      },

      // ── Typography ──────────────────────────────────────────────────────
      typography: {
        fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        fontSize: 14,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 600,
        h1: { fontSize: 32, fontWeight: 700, letterSpacing: "-0.02em" },
        h2: { fontSize: 24, fontWeight: 600, letterSpacing: "-0.01em" },
        h3: { fontSize: 20, fontWeight: 600, letterSpacing: "-0.01em" },
        h4: { fontSize: 18, fontWeight: 600 },
        h5: { fontSize: 16, fontWeight: 600 },
        h6: { fontSize: 14, fontWeight: 600 },
        body1: { fontSize: 14, lineHeight: 1.6 },
        body2: { fontSize: 13, lineHeight: 1.5 },
        caption: { fontSize: 11, color: isLight ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.45)" },
        button: { fontWeight: 500, textTransform: "none", letterSpacing: "0" },
        overline: { letterSpacing: "0.06em", fontWeight: 600, fontSize: 10, textTransform: "uppercase" },
      },

      shape: { borderRadius: "8px" },

      // ── Overrides ───────────────────────────────────────────────────────
      components: {

        // ── Backdrop ──────────────────────────────────────────────────────
        MuiBackdrop: {
          styleOverrides: {
            root: {
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
              backgroundColor: isLight
                ? "rgba(0,0,0,0.32)"
                : "rgba(0,0,0,0.60)",
            },
          },
        },

        // ── Dialog ────────────────────────────────────────────────────────
        MuiDialog: {
          styleOverrides: {
            paper: {
              background: isLight ? "#ffffff !important" : "#0f172a !important",
              backdropFilter: "none !important",
              WebkitBackdropFilter: "none !important",
              border: isLight
                ? "0.5px solid rgba(0,0,0,0.1) !important"
                : "0.5px solid rgba(255,255,255,0.1) !important",
              borderRadius: "14px !important",
              boxShadow: "0 8px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06) !important",
            },
          },
        },

        MuiDialogTitle: {
          styleOverrides: {
            root: {
              fontSize: 16,
              fontWeight: 600,
              color: isLight ? "rgba(0,0,0,0.82)" : "rgba(255,255,255,0.88)",
            },
          },
        },

        // ── Paper ─────────────────────────────────────────────────────────
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: "none",
              marginLeft: 0,
              marginRight: 0,
              backgroundColor: isLight ? "#ffffff" : "#0f172a",
              border: isLight
                ? "0.5px solid rgba(0,0,0,0.07)"
                : "0.5px solid rgba(255,255,255,0.07)",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            },
            rounded: { borderRadius: "8px" },
            elevation1: { boxShadow: "0 1px 3px rgba(0,0,0,0.06)" },
            elevation2: { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
            elevation3: { boxShadow: "0 4px 16px rgba(0,0,0,0.10)" },
          },
        },

        // ── Menus / Popovers ──────────────────────────────────────────────
        MuiMenu: {
          styleOverrides: {
            paper: {
              width: "auto !important",
              maxWidth: "300px !important",
              minWidth: "180px !important",
              background: isLight ? "#ffffff !important" : "#0f172a !important",
              backdropFilter: "none !important",
              WebkitBackdropFilter: "none !important",
              border: isLight
                ? "0.5px solid rgba(0,0,0,0.1) !important"
                : "0.5px solid rgba(255,255,255,0.1) !important",
              borderRadius: "8px !important",
              boxShadow: "0 4px 20px rgba(0,0,0,0.10) !important",
            },
          },
        },

        MuiMenuItem: {
          styleOverrides: {
            root: {
              borderRadius: "6px",
              fontSize: 13,
              "&:hover": {
                background: isLight
                  ? "rgba(3,153,255,0.06)"
                  : "rgba(255,255,255,0.05)",
              },
            },
          },
        },

        MuiPopover: {
          styleOverrides: {
            paper: {
              width: "auto !important",
              maxWidth: "320px !important",
              minWidth: "auto !important",
              background: isLight ? "#ffffff !important" : "#0f172a !important",
              backdropFilter: "none !important",
              WebkitBackdropFilter: "none !important",
              border: isLight
                ? "0.5px solid rgba(0,0,0,0.1) !important"
                : "0.5px solid rgba(255,255,255,0.1) !important",
              borderRadius: "8px !important",
              boxShadow: "0 4px 20px rgba(0,0,0,0.10) !important",
            },
          },
        },

        // ── Outlined Input ────────────────────────────────────────────────
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: "9px",
              background: isLight ? "#f8f7f5" : "rgba(255,255,255,0.04)",
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
              transition: "all 0.18s ease",
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: isLight ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.2)",
              },
              "&.Mui-focused": {
                background: isLight ? "#ffffff" : "#0d1f3c",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: primary,
                borderWidth: "1px",
              },
            },
            notchedOutline: {
              borderColor: isLight ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)",
              borderWidth: "0.5px",
              transition: "all 0.18s ease",
            },
            input: {
              fontFamily: '"Inter", sans-serif',
              fontWeight: 400,
              fontSize: 14,
              "&::placeholder": {
                color: isLight ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.30)",
                opacity: 1,
              },
            },
          },
        },

        // ── Input Label ───────────────────────────────────────────────────
        MuiInputLabel: {
          styleOverrides: {
            outlined: {
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0",
              textTransform: "none",
              color: isLight ? "rgba(0,0,0,0.4)" : "rgba(255,255,255,0.4)",
              "&.MuiInputLabel-shrink": {
                color: isLight ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.55)",
                background: "transparent",
                padding: 0,
                borderRadius: "0px",
                backdropFilter: "none",
                WebkitBackdropFilter: "none",
              },
            },
          },
        },

        MuiSelect: {
          styleOverrides: {
            select: {
              borderRadius: "9px",
              background: isLight ? "#f8f7f5" : "rgba(255,255,255,0.04)",
            },
            icon: {
              color: isLight ? "rgba(0,0,0,0.38)" : "rgba(255,255,255,0.38)",
            },
          },
        },

        // ── Buttons ───────────────────────────────────────────────────────
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: 13,
              letterSpacing: "0",
              padding: "6px 16px",
              boxShadow: "none",
              transition: "all 0.18s ease",
              "&:hover": { transform: "none", boxShadow: "none" },
              "&:active": { transform: "none" },
            },
            containedPrimary: {
              background: "#0399FF",
              boxShadow: "none",
              "&:hover": {
                background: "#0284d4",
                boxShadow: "none",
              },
            },
            containedSecondary: {
              background: isLight ? "rgba(240,64,96,0.08)" : "rgba(240,64,96,0.15)",
              color: "#C82844 !important",
              border: "0.5px solid rgba(230,50,90,0.20)",
              boxShadow: "none",
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
              "&:hover": {
                background: isLight ? "rgba(230,50,90,0.14)" : "rgba(240,64,96,0.22)",
                boxShadow: "none",
              },
            },
            contained: {
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            },
            outlined: {
              borderRadius: "8px",
              background: "transparent",
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
              borderColor: isLight ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.15)",
              color: isLight ? "rgba(0,0,0,0.65)" : "rgba(255,255,255,0.65)",
              "&:hover": {
                background: isLight ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.04)",
                borderColor: isLight ? "rgba(0,0,0,0.25)" : "rgba(255,255,255,0.25)",
                boxShadow: "none",
              },
            },
            outlinedPrimary: {
              color: primary,
              borderColor: primary + "50",
              "&:hover": {
                backgroundColor: primary + "08",
                borderColor: primary,
              },
            },
            text: {
              borderRadius: "8px",
              "&:hover": {
                background: isLight ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.05)",
              },
            },
          },
        },

        // ── Icon Button ───────────────────────────────────────────────────
        MuiIconButton: {
          styleOverrides: {
            root: {
              borderRadius: "8px",
              "&:hover": {
                background: isLight ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.05)",
              },
            },
          },
        },

        // ── Tabs ──────────────────────────────────────────────────────────
        MuiTab: {
          styleOverrides: {
            root: {
              textTransform: "none",
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: "0",
              borderRadius: "0px",
              color: isLight ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.45)",
              transition: "all 0.18s ease",
              "&:hover": { backgroundColor: "transparent", color: isLight ? "rgba(0,0,0,0.65)" : "rgba(255,255,255,0.65)" },
              "&.Mui-selected": { color: primary, fontWeight: 600 },
            },
          },
        },

        MuiTabs: {
          styleOverrides: {
            indicator: {
              backgroundColor: primary,
              height: 2,
              borderRadius: "2px",
            },
          },
        },

        // ── TextField wrapper ──────────────────────────────────────────────
        MuiTextField: {
          styleOverrides: {
            root: {
              "& .MuiOutlinedInput-root": {
                borderRadius: "9px",
                transition: "all 0.18s ease",
              },
            },
          },
        },

        // ── Table ─────────────────────────────────────────────────────────
        MuiTableHead: {
          styleOverrides: {
            root: {
              background: isLight ? "#fafafa" : "#0d1424",
              "& .MuiTableCell-head": {
                fontSize: 10,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: isLight ? "rgba(0,0,0,0.38)" : "rgba(255,255,255,0.38)",
                background: isLight ? "#fafafa" : "#0d1424",
                borderBottom: isLight
                  ? "0.5px solid rgba(0,0,0,0.08)"
                  : "0.5px solid rgba(255,255,255,0.08)",
              },
            },
          },
        },

        MuiTableRow: {
          styleOverrides: {
            root: {
              borderBottom: isLight
                ? "0.5px solid rgba(0,0,0,0.06)"
                : "0.5px solid rgba(255,255,255,0.06)",
              "&:hover": {
                background: isLight ? "rgba(0,0,0,0.02)" : "rgba(255,255,255,0.02)",
              },
            },
          },
        },

        MuiTableCell: {
          styleOverrides: {
            root: {
              borderBottom: isLight
                ? "0.5px solid rgba(0,0,0,0.06)"
                : "0.5px solid rgba(255,255,255,0.06)",
              fontSize: 13,
            },
          },
        },

        // ── AppBar ────────────────────────────────────────────────────────
        MuiAppBar: {
          styleOverrides: {
            root: {
              boxShadow: "none",
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
            },
            colorPrimary: {
              background: isLight ? "#0399FF" : "#0B0F1A",
              borderBottom: isLight
                ? "none"
                : "0.5px solid rgba(255,255,255,0.07)",
              boxShadow: "none",
            },
          },
        },

        // ── Drawer ────────────────────────────────────────────────────────
        MuiDrawer: {
          styleOverrides: {
            paper: {
              border: "none",
              background: isLight ? "#ffffff" : "#0B0F1A",
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
              borderRight: isLight
                ? "0.5px solid rgba(0,0,0,0.07)"
                : "0.5px solid rgba(255,255,255,0.07)",
              boxShadow: "none",
            },
          },
        },

        // ── Container ─────────────────────────────────────────────────────
        MuiContainer: {
          styleOverrides: {
            root: {
              paddingLeft: "0 !important",
              paddingRight: "0 !important",
              maxWidth: "none !important",
              width: "100% !important",
            },
            maxWidthLg: { maxWidth: "none !important" },
            maxWidthMd: { maxWidth: "none !important" },
            maxWidthSm: { maxWidth: "none !important" },
            maxWidthXl: { maxWidth: "none !important" },
            maxWidthXs: { maxWidth: "none !important" },
          },
        },

        // ── Chip ──────────────────────────────────────────────────────────
        MuiChip: {
          styleOverrides: {
            root: {
              fontFamily: '"Inter", sans-serif',
              fontWeight: 500,
              fontSize: 11,
              borderRadius: "6px",
            },
          },
        },

        // ── Tooltip ───────────────────────────────────────────────────────
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              background: "rgba(0,0,0,0.8)",
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
              borderRadius: "8px",
              fontSize: 12,
              fontFamily: '"Inter", sans-serif',
              boxShadow: "0 4px 14px rgba(0,0,0,0.20)",
            },
          },
        },

        // ── Linear Progress ───────────────────────────────────────────────
        MuiLinearProgress: {
          styleOverrides: {
            root: {
              borderRadius: "4px",
              background: "rgba(3,153,255,0.1)",
            },
          },
        },
      },

      // ── Custom tokens ─────────────────────────────────────────────────
      mode,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      calculatedLogoDark: () => {
        if (
          appLogoDark === defaultLogoDark &&
          appLogoLight !== defaultLogoLight
        ) {
          return appLogoLight;
        }
        return appLogoDark;
      },
      calculatedLogoLight: () => {
        if (
          appLogoDark !== defaultLogoDark &&
          appLogoLight === defaultLogoLight
        ) {
          return appLogoDark;
        }
        return appLogoLight;
      },
    },
    locale
  );
};
